/**
 * Coordinate assignment via d3-dag — the "parent centred over its children" look.
 *
 * ## Why a library here and nowhere else
 *
 * The hand-written layout in `projection.ts` does three of Sugiyama's four phases
 * (longest-path layering, median-heuristic crossing reduction, interval-graph lanes
 * for detours) and then places cards in LEFT-PACKED SLOTS. That last part is the one
 * thing the mock does differently, and it is not a tweak: a parent sits above its
 * leftmost child instead of above the middle of its children, so nothing lines up the
 * way a decision tree is read.
 *
 * Centring is not a local fix either — moving one parent shifts its siblings, which
 * shifts their parents. Doing it properly is an optimisation over the whole graph.
 * `coordSimplex` solves it as a LINEAR PROGRAM (globally optimal), which is a strictly
 * better answer than the Brandes–Köpf heuristic that dagre and ELK use, and d3-dag is
 * synchronous and deterministic so it does not break the contract the rest of this
 * surface rests on (same definition → same pixels, which is what makes a draft-vs-live
 * diff meaningful).
 *
 * ELK would also have given orthogonal edge ROUTING, which is the other half of the
 * mock's look — but elkjs is auto-denied on npmpm (license judged Prohibited, manual
 * review required), so routing stays hand-written.
 *
 * ## What this does NOT do
 *
 * Layering, ordering and the checker all stay in `projection.ts`. This module answers
 * one question — where does each card go horizontally — and returns a layer index so
 * the caller can keep its own row-based logic (detour lanes, crossing counts).
 */
import {
  coordSimplex,
  decrossDfs,
  type GraphNode,
  graphStratify,
  layeringLongestPath,
  sugiyama,
} from 'd3-dag';

/**
 * What we hand d3-dag per node. Spelled out because d3-dag's own docs warn that a
 * constant `nodeSize` callback makes TypeScript infer the datum as `never`, which then
 * fails to match the graph — so the callback's parameter has to be typed explicitly.
 */
interface Datum {
  id: string;
  parentIds: string[];
}

export interface DagLayout {
  /** Top-left position per node id. */
  pos: Map<string, { x: number; y: number }>;
  /** Row index per node id, 0 at the top. */
  layerOf: Map<string, number>;
}

export interface DagLayoutInput {
  /** Every node to place. */
  ids: string[];
  /** Edge list, source → target. Targets outside `ids` are ignored. */
  edges: { source: string; target: string }[];
  /**
   * The caller's desired left-to-right order, per row. Used only to fix ORIENTATION —
   * see `mirrorIfReversed`. Rows do not have to match d3-dag's layering; ids it does
   * not know about are ignored.
   */
  orderHint?: string[][];
  /** Per node — cards differ in width, see `cardSize`. */
  nodeW: (id: string) => number;
  /** Per node, so rows can have different heights. */
  nodeH: (id: string) => number;
  colGap: number;
  rowGap: number;
}

/**
 * Lay out a DAG, or return `null` when d3-dag refuses the graph.
 *
 * `null` is a real outcome, not an error path, so the caller can fall back to the grid
 * layout instead of every use site wrapping this in a try/catch.
 *
 * On cycles specifically: d3-dag 1.2.2 BREAKS them internally and still produces a
 * layout — verified against a 3-node A→B→C→A draft, which lays out rather than
 * throwing. That is the behaviour we want (a draft is allowed to be cyclic; reporting
 * the cycle is this surface's job, and the editor has to stay usable while one is on
 * screen), so the cycle case does not reach the fallback. The catch stays for graphs it
 * does reject, whatever those turn out to be.
 */
export function dagLayout(input: DagLayoutInput): DagLayout | null {
  const { ids, edges, nodeW, nodeH, colGap, rowGap, orderHint } = input;
  if (ids.length === 0) return null;

  const known = new Set(ids);
  // Parents, not children: graphStratify wants parentIds, and building it this way
  // drops an edge to a node that does not exist (a dangling target mid-edit) instead
  // of letting d3-dag throw on it.
  const parents = new Map<string, string[]>(ids.map((id) => [id, []]));
  for (const e of edges) {
    if (!known.has(e.source) || !known.has(e.target)) continue;
    const list = parents.get(e.target)!;
    // A duplicate parent (two conditions routing to the same target) is ONE structural
    // edge as far as placement goes; passing it twice makes d3-dag reject the graph.
    if (!list.includes(e.source)) list.push(e.source);
  }

  /*
   * ONLY the d3-dag calls are guarded.
   *
   * The catch below exists to absorb a graph d3-dag refuses. It used to wrap the
   * centre→top-left conversion, the mirror and the normalisation too, which meant a
   * bug of OURS in that post-processing — a bad non-null assertion, a
   * `Math.min(...[])` returning Infinity — would have been swallowed and reported as
   * "d3-dag rejected this", silently dropping the page to the grid layout with nothing
   * logged. A logic error has to be allowed to throw.
   */
  let graph;
  try {
    graph = graphStratify()(
      ids.map((id): Datum => ({ id, parentIds: parents.get(id) ?? [] })),
    );
    const layout = sugiyama()
      .layering(layeringLongestPath())
      /*
       * `decrossDfs`, NOT `decrossTwoLayer`.
       *
       * Branch order is SEMANTIC here: `nextQuestionRules` is first-match-wins, the
       * cards carry ①②③ for exactly that reason, and the five purposes read a–e.
       * `decrossTwoLayer` optimises crossings and is free to permute siblings — it
       * reversed the five branches, putting purpose (e) leftmost. `decrossDfs` walks
       * the graph in input order instead, so left-to-right on screen matches
       * evaluation order in the rules.
       *
       * Giving up crossing optimisation here costs nothing measurable: all five live
       * questionnaires and the merged tree already project with ZERO crossings, and
       * `projection.ts` still has its own median-heuristic `tidyOrder` behind the
       * 「整理交叉」toggle for the cases that do cross.
       */
      .decross(decrossDfs())
      .coord(coordSimplex())
      // Separation is baked into the node size, so a "unit" is one card plus its gap.
      /*
       * Per-node size. d3-dag places rows to fit the TALLEST card in each, so a
       * two-line question no longer has to share a 92px pitch with a one-line one —
       * that mismatch is what left the connectors with nowhere to be drawn.
       */
      .nodeSize(
        (node: GraphNode<Datum, undefined>) =>
          [nodeW(node.data.id) + colGap, nodeH(node.data.id) + rowGap] as const,
      );
    layout(graph);
  } catch {
  // A graph d3-dag will not lay out. The caller falls back to the grid.
  return null;
  }

  /*
   * d3-dag reports CENTRES, and nodes in one layer share a centre y. Converting each
   * card to a top-left corner with its OWN height therefore un-aligns the row: a
   * 2-line card and a 1-line one in the same layer end up 8px apart vertically, for
   * no reason a reader could see.
   *
   * So the row's top comes from its TALLEST card. That is also the only safe choice:
   * d3-dag spaced the layers using the per-node sizes, so the tallest card is what
   * the gap below was computed against — aligning to anything shorter would push a
   * tall card into the next row.
   */
  const centreY = new Map<string, number>();
  const centreX = new Map<string, number>();
  for (const n of graph.nodes()) {
    centreX.set(n.data.id, n.x);
    centreY.set(n.data.id, n.y);
  }
  const tallestAtY = new Map<number, number>();
  for (const [id, cy] of centreY) {
    tallestAtY.set(cy, Math.max(tallestAtY.get(cy) ?? 0, nodeH(id)));
  }

  const pos = new Map<string, { x: number; y: number }>();
  for (const [id, cy] of centreY) {
    pos.set(id, {
      x: centreX.get(id)! - nodeW(id) / 2,
      y: cy - tallestAtY.get(cy)! / 2,
    });
  }
  const ys = [...centreY.values()];

  mirrorIfReversed(pos, orderHint, nodeW);

  // Normalise to origin so the graph does not float away from (0,0) — the canvas
  // fits the viewport to the content, and a leading offset just wastes the frame.
  const minX = Math.min(...[...pos.values()].map((p) => p.x));
  const minY = Math.min(...[...pos.values()].map((p) => p.y));
  for (const [id, p] of pos) pos.set(id, { x: p.x - minX, y: p.y - minY });

  // Layer index from the distinct CENTRE y values — that, not the top edge, is what
  // identifies a layer. Derived rather than read out of d3-dag's internals so this
  // stays independent of its layering representation.
  const rows = [...new Set(ys)].sort((a, b) => a - b);
  const rowIndex = new Map(rows.map((y, i) => [y, i]));
  const layerOf = new Map<string, number>();
  for (const [id, cy] of centreY) {
    layerOf.set(id, rowIndex.get(cy) ?? 0);
  }

  return { pos, layerOf };
}

/**
 * Flip the layout horizontally when that agrees better with the caller's order.
 *
 * d3-dag does not promise which end of a layer the first input node lands on, and in
 * practice it emitted the five purpose branches RIGHT to left: purpose (e) leftmost,
 * (a) at the far right. Branch order is semantic here — `nextQuestionRules` is
 * first-match-wins, the cards carry ①②③ for that reason, and a–e should read the way
 * they are written.
 *
 * A horizontal mirror is an isometry: it preserves the centring simplex solved for and
 * cannot change the number of crossings, so it is free. Rather than assume d3-dag
 * always reverses (which would silently break if it changed), this COUNTS pairwise
 * agreement with the hint both ways and keeps the better one — self-correcting, and a
 * no-op when the orientation already agrees.
 *
 * ⚠️ It needs `nodeW` because `pos` holds LEFT edges, and reflecting a left edge is not
 * the reflection of the card. Mirroring `x` alone shifts every card left by its own
 * width — a constant, and therefore invisible, while all cards were the same width;
 * once widths came from content it put wide cards on top of their neighbours. The
 * reflection of the interval [x, x+w] is [-(x+w), -x], so the new left edge is
 * `-(x + w)`.
 */
function mirrorIfReversed(
  pos: Map<string, { x: number; y: number }>,
  orderHint: string[][] | undefined,
  nodeW: (id: string) => number,
): void {
  if (!orderHint?.length) return;

  let agree = 0;
  let disagree = 0;
  for (const row of orderHint) {
    const placed = row.filter((id) => pos.has(id));
    for (let i = 0; i < placed.length; i++) {
      for (let j = i + 1; j < placed.length; j++) {
        const dx = pos.get(placed[j])!.x - pos.get(placed[i])!.x;
        if (dx > 0) agree++;
        else if (dx < 0) disagree++;
      }
    }
  }
  if (disagree <= agree) return;

  /*
   * Reflect about x = 0 and let the caller's normalisation slide the result back to the
   * origin. Picking an axis inside the extent would work too, but it has to be derived
   * from the cards' RIGHT edges to come out symmetric — and since normalisation follows
   * either way, the simplest correct axis is the cheapest one.
   */
  for (const [id, p] of pos) {
    pos.set(id, { x: -(p.x + nodeW(id)), y: p.y });
  }
}
